/* ==========================================================================
   VAST Hero Slider
   - Basis: CSS scroll-snap (native Touch-Physik), kein Slider-Framework
   - Endlos-Loop über Klon-Slides an beiden Enden (Sprung ohne Animation)
   - Autoplay: pausiert bei Hover (Maus), Drag, Tab im Hintergrund,
     außerhalb des Viewports, im Theme-Editor bei Block-Auswahl
   - Videos: nur das Video des aktiven, sichtbaren Slides läuft
   - Kein Blur-Handling, keine Viewport-Writes, keine MutationObserver
   ========================================================================== */
(() => {
  if (customElements.get('vast-hero-slider')) return;

  const SUPPORTS_SCROLLEND = 'onscrollend' in window;

  class VastHeroSlider extends HTMLElement {
    constructor() {
      super();
      this.onScroll = this.onScroll.bind(this);
      this.onSettle = this.onSettle.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerEnter = this.onPointerEnter.bind(this);
      this.onPointerLeave = this.onPointerLeave.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onVisibility = this.onVisibility.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);
      this.onSlideClick = this.onSlideClick.bind(this);
      this.onPageHide = this.onPageHide.bind(this);
      this.onPageShow = this.onPageShow.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('.vast-hero__track');
      if (!this.track) return;

      this.slides = Array.from(this.track.querySelectorAll('.vast-hero__slide:not([data-clone])'));
      this.count = this.slides.length;
      this.dots = Array.from(this.querySelectorAll('.vast-hero__dot'));

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.autoplay = this.dataset.autoplay === 'true' && !this.reducedMotion;
      this.interval = Math.max(2, parseInt(this.dataset.interval, 10) || 6) * 1000;
      this.loop = this.count > 1;

      this.index = 0;
      this.hover = false;
      this.editorPaused = false;
      this.inView = true;
      this.timer = null;
      this.settleTimer = null;
      this.resizeTimer = null;

      if (this.loop && !this.track.querySelector('[data-clone]')) this.addClones();
      this.hasClones = this.loop;

      this.bind();
      this.position(this.index, false);
      this.updateUI();
      this.syncVideos();
      this.queueAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      clearTimeout(this.settleTimer);
      clearTimeout(this.resizeTimer);
      if (this.track) {
        this.track.removeEventListener('scroll', this.onScroll);
        this.track.removeEventListener('scrollend', this.onSettle);
        this.track.removeEventListener('pointerdown', this.onPointerDown);
      }
      this.removeEventListener('pointerenter', this.onPointerEnter);
      this.removeEventListener('pointerleave', this.onPointerLeave);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('visibilitychange', this.onVisibility);
      if (this.track) this.track.removeEventListener('click', this.onSlideClick);
      window.removeEventListener('pagehide', this.onPageHide);
      window.removeEventListener('pageshow', this.onPageShow);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      if (this.io) this.io.disconnect();
    }

    /* ---------- Setup ---------- */

    addClones() {
      const first = this.slides[0].cloneNode(true);
      const last = this.slides[this.count - 1].cloneNode(true);
      [first, last].forEach((clone) => {
        clone.setAttribute('data-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('tabindex', '-1');
        clone.removeAttribute('data-shopify-editor-block');
        clone.querySelectorAll('img').forEach((img) => {
          img.loading = 'lazy';
          img.removeAttribute('fetchpriority');
        });
        clone.querySelectorAll('video').forEach((video) => {
          video.removeAttribute('autoplay');
          video.preload = 'none';
        });
      });
      this.track.prepend(last);
      this.track.append(first);
    }

    bind() {
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      if (SUPPORTS_SCROLLEND) this.track.addEventListener('scrollend', this.onSettle);
      this.track.addEventListener('pointerdown', this.onPointerDown, { passive: true });
      this.addEventListener('pointerenter', this.onPointerEnter);
      this.addEventListener('pointerleave', this.onPointerLeave);
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      window.addEventListener('resize', this.onResize);
      document.addEventListener('visibilitychange', this.onVisibility);
      this.track.addEventListener('click', this.onSlideClick);
      window.addEventListener('pagehide', this.onPageHide);
      window.addEventListener('pageshow', this.onPageShow);

      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          (entries) => {
            this.inView = entries[0].isIntersecting;
            this.syncVideos();
            if (this.inView) this.queueAutoplay();
            else this.stopAutoplay();
          },
          { threshold: 0.2 }
        );
        this.io.observe(this);
      }

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      }
    }

    /* ---------- Positionierung ---------- */

    slideWidth() {
      return this.track.clientWidth;
    }

    position(i, smooth) {
      const w = this.slideWidth();
      if (!w) return;
      const offset = this.hasClones ? 1 : 0;
      const left = (i + offset) * w;
      const behavior = smooth && !this.reducedMotion ? 'smooth' : 'auto';
      this.track.scrollTo({ left, behavior });
      // Fallback, falls kein scroll/scrollend-Event kommt (z.B. Tab im Hintergrund)
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(this.onSettle, 1000);
    }

    goTo(i) {
      this.position(i, true);
    }

    next() {
      this.position(this.index + 1, true);
    }

    prev() {
      this.position(this.index - 1, true);
    }

    onScroll() {
      this.stopAutoplay();
      if (SUPPORTS_SCROLLEND) return;
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(this.onSettle, 120);
    }

    onSettle() {
      clearTimeout(this.settleTimer);
      const w = this.slideWidth();
      if (!w) return;
      const offset = this.hasClones ? 1 : 0;
      let i = Math.round(this.track.scrollLeft / w) - offset;

      if (this.hasClones) {
        if (i < 0) {
          i = this.count - 1;
          this.jump(i);
        } else if (i >= this.count) {
          i = 0;
          this.jump(i);
        }
      } else {
        i = Math.min(Math.max(i, 0), this.count - 1);
      }

      this.setIndex(i);
      this.queueAutoplay();
    }

    // Sprung von Klon auf echten Slide, ohne Animation
    jump(i) {
      const w = this.slideWidth();
      const offset = this.hasClones ? 1 : 0;
      this.track.scrollTo({ left: (i + offset) * w, behavior: 'auto' });
    }

    setIndex(i) {
      if (i === this.index) return;
      this.index = i;
      this.updateUI();
      this.syncVideos();
    }

    updateUI() {
      this.dots.forEach((dot, n) => {
        const active = n === this.index;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    /* ---------- Video ---------- */

    syncVideos() {
      const active = this.slides[this.index];
      const canPlay = this.inView && !document.hidden;
      this.querySelectorAll('video').forEach((video) => {
        const isActive = !!active && active.contains(video);
        const visible = video.offsetParent !== null; // display:none durch Breakpoint
        if (isActive && visible && canPlay) {
          if (video.paused) this.playVideo(video);
        } else if (!video.paused) {
          video.pause();
        }
      });
    }

    // Safari (macOS/iOS): erster play() auf preload="none" bleibt sonst hängen
    // (schwarzes Bild, Poster weg). Deshalb: einmal load() anstoßen und bei
    // Fehlschlag auf canplay warten und erneut starten.
    playVideo(video) {
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;

      if (video.readyState === 0 && !video.dataset.vhLoaded) {
        video.dataset.vhLoaded = '1';
        try {
          video.load();
        } catch (e) {
          /* noop */
        }
      }

      const attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch(() => {
          if (video.dataset.vhRetry) return;
          video.dataset.vhRetry = '1';
          video.addEventListener(
            'canplay',
            () => {
              const active = this.slides[this.index];
              if (active && active.contains(video) && this.inView && !document.hidden) {
                const again = video.play();
                if (again && typeof again.catch === 'function') again.catch(() => {});
              }
            },
            { once: true }
          );
        });
      }
    }

    /* ---------- Freeze-Frame beim Seitenwechsel ----------
       Safari gibt beim Navigieren den Video-Decoder frei, das <video> wird
       schwarz, bis die neue Seite steht. Deshalb: aktuellen Frame auf ein
       Canvas über dem Video legen, beim Zurück (bfcache) wieder entfernen. */

    activeVideo() {
      const active = this.slides[this.index];
      if (!active) return null;
      return Array.from(active.querySelectorAll('video')).find(
        (v) => v.offsetParent !== null && v.readyState >= 2
      ) || null;
    }

    freezeVideo(video) {
      if (!video || video.dataset.vhFrozen) return;
      const w = video.videoWidth;
      const h = video.videoHeight;
      if (!w || !h) return;
      try {
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(video, 0, 0, w, h);
        canvas.className = 'vast-hero__freeze';
        canvas.setAttribute('aria-hidden', 'true');
        video.parentNode.appendChild(canvas);
        video.dataset.vhFrozen = '1';
      } catch (e) {
        /* Canvas nicht möglich - dann bleibt es beim Browser-Verhalten */
      }
    }

    unfreezeVideos() {
      this.querySelectorAll('.vast-hero__freeze').forEach((c) => c.remove());
      this.querySelectorAll('video[data-vh-frozen]').forEach((v) => delete v.dataset.vhFrozen);
    }

    onSlideClick(e) {
      const slide = e.target.closest('.vast-hero__slide');
      if (!slide || slide.tagName !== 'A' || e.defaultPrevented) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return; // neuer Tab: Seite bleibt
      this.freezeVideo(this.activeVideo());
    }

    onPageHide() {
      this.freezeVideo(this.activeVideo());
    }

    onPageShow(e) {
      if (!e.persisted) return;
      this.unfreezeVideos();
      this.syncVideos();
      this.queueAutoplay();
    }

    /* ---------- Autoplay ---------- */

    queueAutoplay() {
      this.stopAutoplay();
      if (!this.autoplay || !this.loop) return;
      if (this.hover || this.editorPaused || !this.inView || document.hidden) return;
      this.timer = setTimeout(() => this.next(), this.interval);
    }

    stopAutoplay() {
      clearTimeout(this.timer);
      this.timer = null;
    }

    /* ---------- Events ---------- */

    onPointerDown() {
      this.stopAutoplay();
    }

    onPointerEnter(e) {
      if (e.pointerType !== 'mouse') return;
      this.hover = true;
      this.stopAutoplay();
    }

    onPointerLeave(e) {
      if (e.pointerType !== 'mouse') return;
      this.hover = false;
      this.queueAutoplay();
    }

    onClick(e) {
      const arrow = e.target.closest('.vast-hero__arrow');
      if (arrow) {
        e.preventDefault();
        if (arrow.classList.contains('vast-hero__arrow--prev')) this.prev();
        else this.next();
        return;
      }
      const dot = e.target.closest('.vast-hero__dot');
      if (dot) {
        e.preventDefault();
        this.goTo(parseInt(dot.dataset.index, 10) || 0);
      }
    }

    onKeydown(e) {
      if (!this.loop) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.prev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.next();
      }
    }

    onResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.position(this.index, false);
        this.syncVideos();
      }, 150);
    }

    onVisibility() {
      this.syncVideos();
      if (document.hidden) this.stopAutoplay();
      else this.queueAutoplay();
    }

    onBlockSelect(e) {
      const slide = e.target && e.target.closest ? e.target.closest('.vast-hero__slide') : null;
      const i = this.slides.indexOf(slide);
      if (i < 0) return;
      this.editorPaused = true;
      this.stopAutoplay();
      this.position(i, false);
      this.setIndex(i);
    }

    onBlockDeselect(e) {
      if (!e.target || !this.contains(e.target)) return;
      this.editorPaused = false;
      this.queueAutoplay();
    }
  }

  customElements.define('vast-hero-slider', VastHeroSlider);
})();